import { formatValue } from "../../utils/helpers";
import metricMetadataIcon from '../../assets/general/metric-metadata-icon-credc.svg';
import { useState } from "react";
import MetricMetadataModal from "../modals/MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";
import DefaultIcon from '../../assets/general/default-metric.svg?react';
import { useDashboardColor } from "../../context/DashboardColorContext";

interface DoubleMetricCardProps {
    metricOne: DisaggregatedMetric;
    metricTwo: DisaggregatedMetric;
    subtextOne: string;
    subtextTwo: string;
    title: string;
    metadataId: number;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const DoubleMetricCard = ({ metricOne, metricTwo, subtextOne, subtextTwo, title, metadataId, disaggData, geoDetails, geoTag, selectionActive, svg: SvgIcon, showMetadataIcon = true }: DoubleMetricCardProps) => {
    const color = useDashboardColor();

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    // Destructure metricOne properties
    const { versions: versions1 } = metricOne;
    const { value: value1, unit: unit1, percent_calc: percentCalc1 } = versions1[0];

    // Calculate formatted value for metricOne
    const formattedValue1 = unit1 === "ratio" ? `${percentCalc1?.n}/${percentCalc1?.d}` : value1;

    // Destructure metricTwo properties
    const { versions: versions2 } = metricTwo;
    const { value: value2, unit: unit2, percent_calc: percentCalc2 } = versions2[0];

    // Calculate formatted value for metricTwo
    const formattedValue2 = unit2 === "ratio" ? `${percentCalc2?.n}/${percentCalc2?.d}` : value2;

    const parentMetric = disaggData?.find(m => m.id === metadataId);

    const unitDescriptor = parentMetric ? parentMetric.versions[0]?.unit_descriptor : '';

    const IconComponent = SvgIcon || DefaultIcon;

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }

        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(metadataId);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(metadataId, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(metadataId, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="mx-2 my-2">
            <div className="flex flex-col items-start w-full">
                <div className="flex items-center justify-start">
                    { SvgIcon && <IconComponent
                        className="w-[50px] h-[50px]"
                        style={{ color: color }}
                        aria-label={`${name} icon`}
                        role="img"
                    /> }
                    
                    <div className="flex flex-col items-start ml-2">
                        <p className="text-sm font-barlow-condensed font-bold" style={{ color: color }}>{subtextOne}</p>
                        <h2 className="text-3xl font-barlow-condensed font-bold -mt-1" style={{ color: color }}>{formatValue(formattedValue1, unit1, metricOne.id)}</h2>
                    </div>
                    <div className="flex flex-col items-start ml-2">
                        <p className="text-sm font-barlow-condensed font-bold" style={{ color: color }}>{subtextTwo}</p>
                        <h2 className="text-3xl font-barlow-condensed font-bold -mt-1" style={{ color: color }}>{formatValue(formattedValue2, unit2, metricTwo.id)}</h2>
                    </div>
                </div>
                <div className="flex my-1 max-w-44">
                    <p className={`text-port-text text-xs font-bold`}>{title}</p>

                    {showMetadataIcon && 
                        <div className="shrink-0 -mt-[3px] ml-2 group relative">
                            <button onClick={handleClick} title="View more information">
                                <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                            </button>
                        </div>
                    }
                </div>
                {unitDescriptor && <p className={`text-port-text text-xs -mt-1`}>{unitDescriptor}</p>}
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={metadataId}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                geoTag={geoTag}
                svg={SvgIcon}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default DoubleMetricCard;
